<template>
  <div>
    <headerSection />
    <main class="member-page history-page">
      <div class="member-page__content">
        <MemberPageHeading
          eyebrow="Account activity"
          title="ประวัติธุรกรรม"
          subtitle="ตรวจสอบเงินเข้าออก โบนัส และสถานะรายการล่าสุดได้ในที่เดียว"
        />

        <section class="member-card member-card--hero" aria-label="สรุปธุรกรรม">
          <div class="member-hero__top">
            <div>
              <div class="member-hero__label">ยอดสุทธิจากรายการล่าสุด</div>
              <div :class="['member-hero__amount', historyTotals.net < 0 ? 'member-negative' : '']">
                {{ historyTotals.net >= 0 ? '+' : '−' }}{{ numberWithCommas(Math.abs(historyTotals.net)) }}
                <span class="member-hero__currency">บาท</span>
              </div>
            </div>
            <div class="member-hero__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 18V9m5 9V5m5 13v-7m5 7V3" />
                <path d="m3 7 5-4 5 5 7-6" />
              </svg>
            </div>
          </div>
          <div class="member-divider" />
          <div class="member-stats">
            <div>
              <span class="member-stat__label">เงินเข้า</span>
              <strong class="member-stat__value member-positive">+{{ numberWithCommas(historyTotals.income) }}</strong>
            </div>
            <div>
              <span class="member-stat__label">เงินออก</span>
              <strong class="member-stat__value member-negative">{{ historyTotals.outcome > 0 ? '−' : '' }}{{ numberWithCommas(historyTotals.outcome) }}</strong>
            </div>
          </div>
        </section>

        <div class="member-section-head">
          <h2 class="member-section-head__title">รายการล่าสุด</h2>
          <span class="member-section-head__meta">{{ history.length }} รายการ</span>
        </div>

        <div class="member-chips" aria-label="กรองประเภทธุรกรรม">
          <button
            v-for="filter in historyFilters"
            :key="filter.value"
            type="button"
            :class="['member-chip', { 'member-chip--active': activeHistoryFilter === filter.value }]"
            @click="activeHistoryFilter = filter.value"
          >
            {{ filter.label }}
          </button>
        </div>

        <div v-if="filteredHistory.length" class="member-transaction-list">
          <article
            v-for="item in filteredHistory"
            :key="item._displayKey"
            class="member-transaction"
          >
            <div :class="['member-transaction__icon', `member-transaction__icon--${item._category}`]" aria-hidden="true">
              {{ item._icon }}
            </div>
            <div class="member-transaction__copy">
              <strong class="member-transaction__title">{{ item.type }}</strong>
              <small class="member-transaction__date">{{ item.createdateShow }}</small>
            </div>
            <div class="member-transaction__value">
              <strong :class="['member-transaction__amount', item._amountClass]">
                {{ item._sign }}{{ numberWithCommas(item.qty) }}
              </strong>
              <small :class="['member-transaction__status', item._statusClass]">{{ item._statusLabel }}</small>
            </div>
          </article>
        </div>
        <div v-else class="member-empty">ยังไม่มีรายการในหมวดนี้</div>
      </div>
    </main>
    <footerSection />
  </div>
</template>

<script>
// color: #FB667A;
import firebase from 'firebase/app'
import 'firebase/database'
import router from './../router'
import moment from 'moment-timezone'
import _ from 'lodash'
export default {
  name: 'Promotion',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue'),
    MemberPageHeading: () => import('../components/member/MemberPageHeading.vue')
  },
  computed: {
    user () {
      return this.$store.state.user.user
    },
    normalizedHistory () {
      return this.history.map((item, index) => {
        const category = this.getHistoryCategory(item.type)
        const status = this.getHistoryStatus(item.status)
        return {
          ...item,
          _displayKey: item.key || `${item.createdate || index}-${item.type || 'transaction'}`,
          _category: category,
          _icon: this.getHistoryIcon(category),
          _sign: category === 'withdraw' ? '−' : '+',
          _amountClass: category === 'withdraw' ? 'member-negative' : category === 'bonus' ? 'member-gold' : category === 'game' ? 'member-blue' : 'member-positive',
          _statusClass: status.className,
          _statusLabel: status.label
        }
      })
    },
    filteredHistory () {
      if (this.activeHistoryFilter === 'all') return this.normalizedHistory
      return this.normalizedHistory.filter(item => item._category === this.activeHistoryFilter)
    },
    historyTotals () {
      return this.normalizedHistory.reduce((totals, item) => {
        const value = Number(item.qty || 0)
        if (item._category === 'withdraw') totals.outcome += value
        else totals.income += value
        totals.net = totals.income - totals.outcome
        return totals
      }, { income: 0, outcome: 0, net: 0 })
    }
  },
  data () {
    return {
      history: [],
      activeHistoryFilter: 'all',
      historyFilters: [
        { value: 'all', label: 'ทั้งหมด' },
        { value: 'deposit', label: 'ฝากเงิน' },
        { value: 'withdraw', label: 'ถอนเงิน' },
        { value: 'bonus', label: 'โบนัส' },
        { value: 'game', label: 'กิจกรรม' }
      ]
    }
  },
  methods: {
    getHistoryCategory (type) {
      if (['ถอน', 'ถอนเงิน'].includes(type)) return 'withdraw'
      if (['ฝาก', 'ฝากเงิน'].includes(type)) return 'deposit'
      if (['ฝากต่อเนื่อง', 'พันธมิตร', 'คืนยอดเล่น'].includes(type)) return 'bonus'
      return 'game'
    },
    getHistoryIcon (category) {
      return {
        deposit: '↙',
        withdraw: '↗',
        bonus: '✦',
        game: '◆'
      }[category]
    },
    getHistoryStatus (status) {
      if (status === 'ดำเนินการแล้ว') return { className: 'member-transaction__status--success', label: 'สำเร็จ' }
      if (status === 'ยกเลิก') return { className: 'member-transaction__status--cancelled', label: 'ยกเลิก' }
      return { className: 'member-transaction__status--pending', label: 'กำลังดำเนินการ' }
    },
    scrollToTop () {
      window.scrollTo(0, 0)
    },
    numberWithCommas (x) {
      if (!x) x = 0
      return Number(x).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getUserId () {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        return _user
      } else {
        firebase.auth().signOut()
        localStorage.removeItem('userData')
        router.replace('/')
      }
    }
  },
  async mounted () {
    this.scrollToTop()
    this.$store.commit('user/SET_LOADING', true)
    const user = this.getUserId()
    const history = []
    let lastdate = ''
    await firebase.firestore().collection('history').where('userkey', '==', user.key).orderBy('createdate', 'desc').limit(500).get().then((snapshot) => {
      snapshot.forEach((doc) => {
        if (doc.data().qty) {
          const data = doc.data()
          data.key = doc.id
          data.createdateShow = moment(data.createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm')
          lastdate = data.createdate
          history.push(data)
        }
      })
    })
    await firebase.firestore().collection('spin').where('userkey', '==', user.key).where('createdate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        let game = 'วงล้อ'
        if (doc.data().game === '3boxs') {
          game = 'เปิดหีบสมบัติ'
        } else if (doc.data().game === 'roulet') {
          game = 'รูเล็ต'
        } else if (doc.data().game === 'checkin') {
          game = 'check-in'
        } else if (doc.data().game === 'code') {
          game = 'code'
        } else if (doc.data().game === 'scratch') {
          game = 'ขูดๆถูๆ'
        } else if (doc.data().game === 'gift') {
          game = 'เปิดกล่องของขวัญ'
        }

        history.push({
          key: doc.id,
          type: game,
          createdate: doc.data().createdate,
          createdateShow: moment(doc.data().createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:ss'),
          qty: doc.data().value,
          status: 'ดำเนินการแล้ว'
        })
      })
    })
    await firebase.firestore().collection('continuous_deposit_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'ฝากต่อเนื่อง',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:ss'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })
    await firebase.firestore().collection('partner_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'พันธมิตร',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:ss'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })
    await firebase.firestore().collection('cashback_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'คืนยอดเล่น',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:ss'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    this.history = _.orderBy(history, ['createdate'], ['desc'])
    this.$store.commit('user/SET_LOADING', false)
  }
}
</script>
<style scoped>
.history-page .member-empty {
  margin-top: 13px;
}
</style>
